import axios, { AxiosResponse } from "axios";
import { WebAPI, IFailCallbackParam, ISelectProjectParam } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";
import { ProjaectMemo, ProjaectName } from "./common";

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIでプロジェクトの更新が成功した場合に呼び出されます
   * @callback success
   * @param {number} status - httpステータス
   */
  success: (status: number) => void;
}

export interface IUpdateProjectsRequestModel {
  /**
   * プロジェクト名
   */
  name: string;
  /**
   * メモ
   */
  memo: string;
}

function AxiosPut(
  url: string,
  params: IUpdateProjectsRequestModel,
  token?: string
) {
  if (token) {
    return axios.put(url, JSON.stringify(params), {
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    });
  } else {
    return axios.put(url, JSON.stringify(params), {
      headers: { "Content-Type": "application/json" },
    });
  }
}

interface IAxiosInternalParams extends ICallbackParams {
  promise: Promise<AxiosResponse>;
}

function AxiosInternal(params: IAxiosInternalParams) {
  params.promise
    .then((res) => {
      params.success(res.status);
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    });
}

export interface IUpdateProjectParams
  extends ISelectProjectParam,
    IUpdateProjectsRequestModel,
    ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
}

/**
 * プロジェクトを更新します
 * @function updateProject
 * @param {IUpdateProjectParams} params - プロジェクト更新パラメータ
 */
export function updateProject(params: IUpdateProjectParams): void {
  const { authService, projectid, name, memo, etag } = params;
  const req: IUpdateProjectsRequestModel = {
    name: ProjaectName(name),
    memo: ProjaectMemo(memo),
  };
  const url = WebAPI.UPDATE_PROJECT(projectid, etag);
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          promise: AxiosPut(url, req, token),
          success: params.success,
          fail: (id, status) => {
            if (status === 401) {
              updateProjectRetry401(authService, url, req, params);
            } else {
              params.fail(id, status);
            }
          },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      promise: AxiosPut(url, req),
      success: params.success,
      fail: params.fail,
    });
  }
}

/**
 * プロジェクトを更新します
 * @function updateProjectRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {IUpdateProjectsRequestModel} req - リクエストパラメータ
 * @param {ICallbackParams} params - コールバックパラメータ
 */
function updateProjectRetry401(
  authService: AuthService,
  url: string,
  req: IUpdateProjectsRequestModel,
  params: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        promise: AxiosPut(url, req, token),
        success: params.success,
        fail: params.fail,
      });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    },
  });
}
